import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Box,
    Grid,
    IconButton,
    Chip,
    InputAdornment,
    Fade,
    Slide,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Collapse,
    Tooltip,
    CircularProgress,
    Avatar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@material-ui/core';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Category as CategoryIcon,
    Save as SaveIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Clear as ClearIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    SubdirectoryArrowRight as SubIcon,
    Folder as FolderIcon,
    FolderOpen as FolderOpenIcon,
    Image as ImageIcon,
    ArrowBack as ArrowBackIcon,
    PhotoCamera as PhotoCameraIcon,
} from '@material-ui/icons';
import { makeStyles, createTheme, ThemeProvider } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from './Navbar';
import CategorySelector from './CategorySelector';

const theme = createTheme({
    palette: {
        primary: {
            main: '#667eea',
            light: '#818cf8',
            dark: '#5b21b6',
        },
        secondary: {
            main: '#f59e0b',
            light: '#fbbf24',
            dark: '#d97706',
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff',
        },
        success: {
            main: '#10b981',
            light: '#34d399',
            dark: '#059669',
        },
        error: {
            main: '#ef4444',
            light: '#f87171',
            dark: '#dc2626',
        },
    },
    typography: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
        h4: {
            fontWeight: 700,
            color: '#1e293b',
        },
        h5: {
            fontWeight: 600,
            color: '#334155',
        },
        h6: {
            fontWeight: 600,
            color: '#475569',
        },
        body1: {
            color: '#64748b',
        },
    },
    shape: {
        borderRadius: 16,
    },
});

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        paddingBottom: theme.spacing(4),
    },
    header: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: theme.spacing(6, 0),
        marginBottom: theme.spacing(4),
        boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat',
        },
    },
    headerContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        zIndex: 1,
    },
    headerTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    headerIcon: {
        fontSize: '3rem',
        opacity: 0.9,
    },
    backButton: {
        marginBottom: theme.spacing(2),
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(135deg, #5b21b6 0%, #6b46c1 100%)',
        },
    },
    formCard: {
        marginBottom: theme.spacing(4),
        borderRadius: theme.shape.borderRadius,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        overflow: 'hidden',
    },
    formHeader: {
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        padding: theme.spacing(3),
        borderBottom: '1px solid #e2e8f0',
    },
    formContent: {
        padding: theme.spacing(4),
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.shape.borderRadius,
            transition: 'all 0.3s ease',
            '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.light,
                },
            },
            '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                },
            },
        },
        '& .MuiInputLabel-root': {
            fontWeight: 500,
        },
    },
    submitButton: {
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        padding: theme.spacing(1.5, 4),
        borderRadius: theme.shape.borderRadius,
        fontWeight: 600,
        fontSize: '1rem',
        textTransform: 'none',
        boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)',
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, #5b21b6 100%)`,
        },
        '&:disabled': {
            opacity: 0.6,
            transform: 'none',
        },
    },
    categoryCard: {
        borderRadius: theme.shape.borderRadius,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        overflow: 'hidden',
        marginBottom: theme.spacing(3),
    },
    categoryHeader: {
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        padding: theme.spacing(3),
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    searchField: {
        minWidth: 300,
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            borderRadius: theme.shape.borderRadius,
        },
    },
    categoryItem: {
        padding: theme.spacing(3),
        borderBottom: '1px solid #e2e8f0',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: '#f8fafc',
            transform: 'translateX(4px)',
        },
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    mainCategory: {
        backgroundColor: '#ffffff',
        borderLeft: `4px solid ${theme.palette.primary.main}`,
    },
    subCategory: {
        backgroundColor: '#f8fafc',
        marginLeft: theme.spacing(4),
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '0 8px 8px 0',
    },
    categoryContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    categoryInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flex: 1,
    },
    categoryName: {
        fontSize: '1.1rem',
        fontWeight: 600,
        color: '#1e293b',
    },
    subCategoryName: {
        fontSize: '1rem',
        fontWeight: 500,
        color: '#475569',
    },
    categoryActions: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
    },
    actionButton: {
        borderRadius: 8,
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-1px)',
        },
    },
    saveButton: {
        backgroundColor: theme.palette.success.main,
        color: 'white',
        '&:hover': {
            backgroundColor: theme.palette.success.dark,
        },
    },
    deleteButton: {
        backgroundColor: theme.palette.error.main,
        color: 'white',
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        },
    },
    expandButton: {
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
        },
    },
    statsContainer: {
        marginBottom: theme.spacing(3),
    },
    statCard: {
        padding: theme.spacing(3),
        textAlign: 'center',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0',
        borderRadius: theme.shape.borderRadius,
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
        },
    },
    statNumber: {
        fontSize: '2rem',
        fontWeight: 700,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(0.5),
    },
    statLabel: {
        color: '#64748b',
        fontWeight: 500,
        fontSize: '0.9rem',
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(6),
        color: '#64748b',
    },
    emptyIcon: {
        fontSize: '4rem',
        marginBottom: theme.spacing(2),
        opacity: 0.5,
    },
    editableField: {
        '& .MuiTextField-root': {
            '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8fafc',
                borderRadius: 8,
                '&:hover': {
                    backgroundColor: 'white',
                },
                '&.Mui-focused': {
                    backgroundColor: 'white',
                },
            },
        },
    },
    hierarchyIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    categoryImage: {
        width: 64,
        height: 64,
        borderRadius: 8,
        border: '2px solid #e2e8f0',
        marginRight: theme.spacing(2),
    },
    imagePreview: {
        width: 80,
        height: 80,
        borderRadius: 8,
        border: '2px solid #e2e8f0',
        objectFit: 'cover',
    },
    fileInput: {
        display: 'none',
    },
    imageUploadButton: {
        border: '2px dashed #cbd5e0',
        borderRadius: 8,
        padding: theme.spacing(2),
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: 'rgba(102, 126, 234, 0.05)',
        },
    },
    categoryFormContainer: {
        background: '#f8fafc',
        padding: theme.spacing(2),
        borderRadius: 8,
        marginTop: theme.spacing(2),
    },
}));

function CategoryManager() {
    const classes = useStyles();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryImage, setNewCategoryImage] = useState(null);
    const [newSubCategoryName, setNewSubCategoryName] = useState('');
    const [newSubCategoryImage, setNewSubCategoryImage] = useState(null);
    const [selectedParentId, setSelectedParentId] = useState('');
    const [expandedCategories, setExpandedCategories] = useState({});
    const [showForm, setShowForm] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [editingValue, setEditingValue] = useState('');
    const [editingImage, setEditingImage] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        filterCategories();
    }, [categories, searchTerm]);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await axios.get('https://backend.csibon.app/categories_easy_drinks/categories');
            setCategories(response.data);
            // Expand all main categories by default
            const expanded = {};
            response.data.forEach(cat => {
                expanded[cat.id] = true;
            });
            setExpandedCategories(expanded);
        } catch (error) {
            console.error('Erreur lors du chargement des catégories:', error);
        } finally {
            setLoading(false);
        }
    };

    const filterCategories = () => {
        if (!searchTerm.trim()) {
            setFilteredCategories(categories);
            return;
        }

        const filtered = categories.filter(category => {
            const matchesMain = category.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesSub = category.subCategories?.some(sub => 
                sub.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
            return matchesMain || matchesSub;
        }).map(category => {
            if (category.subCategories) {
                return {
                    ...category,
                    subCategories: category.subCategories.filter(sub =>
                        sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        category.name.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                };
            }
            return category;
        });

        setFilteredCategories(filtered);
    };

    const handleAddCategory = async (e) => {
        e.preventDefault();
        if (!newCategoryName.trim()) return;

        try {
            setSaving(true);
            const formData = new FormData();
            formData.append('name', newCategoryName);
            if (newCategoryImage) {
                formData.append('image', newCategoryImage);
            }

            const response = await axios.post('https://backend.csibon.app/categories_easy_drinks/categories', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            await fetchCategories();
            setNewCategoryName('');
            setNewCategoryImage(null);
            console.log('Catégorie ajoutée avec succès');
        } catch (error) {
            console.error('Erreur lors de l\'ajout de la catégorie:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleAddSubCategory = async (e) => {
        e.preventDefault();
        if (!newSubCategoryName.trim() || !selectedParentId) return;

        try {
            setSaving(true);
            const formData = new FormData();
            formData.append('name', newSubCategoryName);
            formData.append('parent_id', selectedParentId);
            if (newSubCategoryImage) {
                formData.append('image', newSubCategoryImage);
            }

            const response = await axios.post('https://backend.csibon.app/categories_easy_drinks/categories', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            await fetchCategories();
            setNewSubCategoryName('');
            setNewSubCategoryImage(null);
            setSelectedParentId('');
            console.log('Sous-catégorie ajoutée avec succès');
        } catch (error) {
            console.error('Erreur lors de l\'ajout de la sous-catégorie:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (id, currentName, currentImage) => {
        setEditingId(id);
        setEditingValue(currentName);
        setEditingImage(null);
    };

    const handleSaveEdit = async () => {
        if (!editingValue.trim()) return;

        try {
            const formData = new FormData();
            formData.append('name', editingValue);
            if (editingImage) {
                formData.append('image', editingImage);
            }

            await axios.put(`https://backend.csibon.app/categories_easy_drinks/categories/${editingId}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            await fetchCategories();
            setEditingId(null);
            setEditingValue('');
            setEditingImage(null);
            console.log('Catégorie mise à jour avec succès');
        } catch (error) {
            console.error('Erreur lors de la mise à jour:', error);
        }
    };

    const handleDeleteClick = (category, isSubCategory = false) => {
        setSelectedCategory({ ...category, isSubCategory });
        setDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedCategory) return;

        try {
            await axios.delete(`https://backend.csibon.app/categories_easy_drinks/categories/${selectedCategory.id}`);
            await fetchCategories();
            setDeleteDialogOpen(false);
            setSelectedCategory(null);
            console.log('Catégorie supprimée avec succès');
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
        }
    };

    const toggleExpanded = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const toggleForm = (path, formType) => {
        const pathKey = `${formType}-${path}`;
        setShowForm(prev => ({ ...prev, [pathKey]: !prev[pathKey] }));
    };

    const getTotalSubCategories = () => {
        return categories.reduce((total, cat) => total + (cat.subCategories?.length || 0), 0);
    };

    const handleImageChange = (e, setImageCallback) => {
        const file = e.target.files[0];
        if (file) {
            setImageCallback(file);
        }
    };

    const renderSubCategories = (subCategories, parentId) => {
        if (!subCategories || subCategories.length === 0) return null;

        return (
            <Collapse in={expandedCategories[parentId]} timeout="auto" unmountOnExit>
                <Box>
                    {subCategories.map((subCategory) => (
                        <div key={subCategory.id} className={`${classes.categoryItem} ${classes.subCategory}`}>
                            <div className={classes.categoryContent}>
                                <div className={classes.categoryInfo}>
                                    <div className={classes.hierarchyIndicator}>
                                        <SubIcon color="secondary" />
                                    </div>
                                    {subCategory.image && (
                                        <Avatar 
                                            src={subCategory.image} 
                                            className={classes.categoryImage}
                                            variant="rounded"
                                        >
                                            <ImageIcon />
                                        </Avatar>
                                    )}
                                    {editingId === subCategory.id ? (
                                        <Box className={classes.editableField} display="flex" gap={2} alignItems="center" flex={1}>
                                            <TextField
                                                value={editingValue}
                                                onChange={(e) => setEditingValue(e.target.value)}
                                                variant="outlined"
                                                size="small"
                                                autoFocus
                                            />
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleImageChange(e, setEditingImage)}
                                                className={classes.fileInput}
                                                id={`edit-image-${subCategory.id}`}
                                            />
                                            <label htmlFor={`edit-image-${subCategory.id}`}>
                                                <IconButton component="span" size="small">
                                                    <PhotoCameraIcon />
                                                </IconButton>
                                            </label>
                                        </Box>
                                    ) : (
                                        <Typography 
                                            className={classes.subCategoryName}
                                            onClick={() => handleEdit(subCategory.id, subCategory.name, subCategory.image)}
                                            style={{ cursor: 'pointer', flex: 1 }}
                                        >
                                            {subCategory.name}
                                        </Typography>
                                    )}
                                </div>
                                <div className={classes.categoryActions}>
                                    {editingId === subCategory.id ? (
                                        <>
                                            <Tooltip title="Sauvegarder">
                                                <IconButton
                                                    className={`${classes.actionButton} ${classes.saveButton}`}
                                                    onClick={handleSaveEdit}
                                                    size="small"
                                                >
                                                    <SaveIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Annuler">
                                                <IconButton
                                                    className={classes.actionButton}
                                                    onClick={() => setEditingId(null)}
                                                    size="small"
                                                >
                                                    <ClearIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <>
                                            <Tooltip title="Modifier">
                                                <IconButton
                                                    className={`${classes.actionButton} ${classes.saveButton}`}
                                                    onClick={() => handleEdit(subCategory.id, subCategory.name, subCategory.image)}
                                                    size="small"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Supprimer">
                                                <IconButton
                                                    className={`${classes.actionButton} ${classes.deleteButton}`}
                                                    onClick={() => handleDeleteClick(subCategory, true)}
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Formulaire d'ajout de sous-sous-catégorie */}
                            {showForm[`add-${subCategory.id}`] && (
                                <div className={classes.categoryFormContainer}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Ajouter une sous-catégorie à "{subCategory.name}"
                                    </Typography>
                                    <Box display="flex" gap={2} alignItems="center">
                                        <TextField
                                            placeholder="Nom de la sous-catégorie"
                                            variant="outlined"
                                            size="small"
                                            className={classes.textField}
                                        />
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className={classes.fileInput}
                                            id={`add-sub-image-${subCategory.id}`}
                                        />
                                        <label htmlFor={`add-sub-image-${subCategory.id}`}>
                                            <IconButton component="span" size="small">
                                                <PhotoCameraIcon />
                                            </IconButton>
                                        </label>
                                        <Button
                                            size="small"
                                            startIcon={<AddIcon />}
                                            className={classes.submitButton}
                                            style={{ minWidth: 'auto' }}
                                        >
                                            Ajouter
                                        </Button>
                                    </Box>
                                </div>
                            )}
                        </div>
                    ))}
                </Box>
            </Collapse>
        );
    };

    if (loading) {
        return (
            <ThemeProvider theme={theme}>
                <div className={classes.root}>
                    <Navbar />
                    <div className={classes.loadingContainer}>
                        <CircularProgress size={40} />
                        <Typography variant="h6" style={{ marginLeft: 16 }}>
                            Chargement des catégories...
                        </Typography>
                    </div>
                </div>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <div className={classes.root}>
                <Navbar />
                
                {/* Header */}
                <div className={classes.header}>
                    <Container>
                        <div className={classes.headerContent}>
                            <div className={classes.headerTitle}>
                                <CategoryIcon className={classes.headerIcon} />
                                <div>
                                    <Typography variant="h4">
                                        Gestion des Catégories
                                    </Typography>
                                    <Typography variant="body1" style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '1.1rem' }}>
                                        Organisez vos catégories et sous-catégories avec images
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    </Container>
                </div>

                <Container>
                    {/* Bouton retour */}
                    <Button
                        onClick={() => navigate('/accueil')}
                        startIcon={<ArrowBackIcon />}
                        className={classes.backButton}
                        style={{ marginBottom: 20 }}
                    >
                        Retour à l'accueil
                    </Button>

                    {/* Statistiques */}
                    <Fade in={true} timeout={1000}>
                        <Grid container spacing={3} className={classes.statsContainer}>
                            <Grid item xs={12} sm={4}>
                                <Card className={classes.statCard}>
                                    <CardContent>
                                        <div className={classes.statNumber}>{categories.length}</div>
                                        <div className={classes.statLabel}>Catégories principales</div>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Card className={classes.statCard}>
                                    <CardContent>
                                        <div className={classes.statNumber}>{getTotalSubCategories()}</div>
                                        <div className={classes.statLabel}>Sous-catégories</div>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Card className={classes.statCard}>
                                    <CardContent>
                                        <div className={classes.statNumber}>{filteredCategories.length}</div>
                                        <div className={classes.statLabel}>Résultats affichés</div>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Fade>

                    {/* Formulaires d'ajout */}
                    <Slide direction="up" in={true} timeout={800}>
                        <Card className={classes.formCard}>
                            <div className={classes.formHeader}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <AddIcon color="primary" />
                                    <Typography variant="h6">
                                        Ajouter des catégories
                                    </Typography>
                                </Box>
                            </div>
                            <div className={classes.formContent}>
                                <Grid container spacing={4}>
                                    {/* Ajouter une catégorie principale */}
                                    <Grid item xs={12} md={6}>
                                        <Box>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                <FolderIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                                Nouvelle catégorie principale
                                            </Typography>
                                            <form onSubmit={handleAddCategory}>
                                                <TextField
                                                    fullWidth
                                                    label="Nom de la catégorie"
                                                    variant="outlined"
                                                    value={newCategoryName}
                                                    onChange={(e) => setNewCategoryName(e.target.value)}
                                                   className={classes.textField}
                                                   margin="normal"
                                                   InputProps={{
                                                       startAdornment: (
                                                           <InputAdornment position="start">
                                                               <CategoryIcon color="action" />
                                                           </InputAdornment>
                                                       ),
                                                   }}
                                                   required
                                               />
                                               
                                               {/* Upload d'image pour catégorie principale */}
                                               <Box mt={2} mb={2}>
                                                   <input
                                                       type="file"
                                                       accept="image/*"
                                                       onChange={(e) => handleImageChange(e, setNewCategoryImage)}
                                                       className={classes.fileInput}
                                                       id="new-category-image"
                                                   />
                                                   <label htmlFor="new-category-image">
                                                       <div className={classes.imageUploadButton}>
                                                           <PhotoCameraIcon color="action" />
                                                           <Typography variant="body2" color="textSecondary">
                                                               Ajouter une image
                                                           </Typography>
                                                       </div>
                                                   </label>
                                                   {newCategoryImage && (
                                                       <Box mt={2}>
                                                           <img 
                                                               src={URL.createObjectURL(newCategoryImage)} 
                                                               alt="Preview" 
                                                               className={classes.imagePreview}
                                                           />
                                                       </Box>
                                                   )}
                                               </Box>

                                               <Button
                                                   type="submit"
                                                   className={classes.submitButton}
                                                   startIcon={<AddIcon />}
                                                   disabled={saving || !newCategoryName.trim()}
                                                   fullWidth
                                                   style={{ marginTop: 16 }}
                                               >
                                                   {saving ? 'Ajout en cours...' : 'Ajouter la catégorie'}
                                               </Button>
                                           </form>
                                       </Box>
                                   </Grid>

                                   {/* Ajouter une sous-catégorie */}
                                   <Grid item xs={12} md={6}>
                                       <Box>
                                           <Typography variant="h6" gutterBottom color="secondary">
                                               <SubIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                               Nouvelle sous-catégorie
                                           </Typography>
                                           <form onSubmit={handleAddSubCategory}>
                                               <FormControl fullWidth variant="outlined" margin="normal" className={classes.textField}>
                                                   <InputLabel>Catégorie parente</InputLabel>
                                                   <Select
                                                       value={selectedParentId}
                                                       onChange={(e) => setSelectedParentId(e.target.value)}
                                                       label="Catégorie parente"
                                                       required
                                                   >
                                                       {categories.map((cat) => (
                                                           <MenuItem key={cat.id} value={cat.id}>
                                                               {cat.name}
                                                           </MenuItem>
                                                       ))}
                                                   </Select>
                                               </FormControl>

                                               <TextField
                                                   fullWidth
                                                   label="Nom de la sous-catégorie"
                                                   variant="outlined"
                                                   value={newSubCategoryName}
                                                   onChange={(e) => setNewSubCategoryName(e.target.value)}
                                                   className={classes.textField}
                                                   margin="normal"
                                                   InputProps={{
                                                       startAdornment: (
                                                           <InputAdornment position="start">
                                                               <SubIcon color="action" />
                                                           </InputAdornment>
                                                       ),
                                                   }}
                                                   required
                                               />

                                               {/* Upload d'image pour sous-catégorie */}
                                               <Box mt={2} mb={2}>
                                                   <input
                                                       type="file"
                                                       accept="image/*"
                                                       onChange={(e) => handleImageChange(e, setNewSubCategoryImage)}
                                                       className={classes.fileInput}
                                                       id="new-subcategory-image"
                                                   />
                                                   <label htmlFor="new-subcategory-image">
                                                       <div className={classes.imageUploadButton}>
                                                           <PhotoCameraIcon color="action" />
                                                           <Typography variant="body2" color="textSecondary">
                                                               Ajouter une image
                                                           </Typography>
                                                       </div>
                                                   </label>
                                                   {newSubCategoryImage && (
                                                       <Box mt={2}>
                                                           <img 
                                                               src={URL.createObjectURL(newSubCategoryImage)} 
                                                               alt="Preview" 
                                                               className={classes.imagePreview}
                                                           />
                                                       </Box>
                                                   )}
                                               </Box>

                                               <Button
                                                   type="submit"
                                                   className={classes.submitButton}
                                                   startIcon={<AddIcon />}
                                                   disabled={saving || !newSubCategoryName.trim() || !selectedParentId}
                                                   fullWidth
                                                   style={{ marginTop: 16, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
                                               >
                                                   {saving ? 'Ajout en cours...' : 'Ajouter la sous-catégorie'}
                                               </Button>
                                           </form>
                                       </Box>
                                   </Grid>
                               </Grid>
                           </div>
                       </Card>
                   </Slide>

                   {/* Liste des catégories */}
                   <Slide direction="up" in={true} timeout={1200}>
                       <Card className={classes.categoryCard}>
                           <div className={classes.categoryHeader}>
                               <Typography variant="h6" color="primary">
                                   Liste des catégories ({filteredCategories.length})
                               </Typography>
                               <TextField
                                   className={classes.searchField}
                                   variant="outlined"
                                   placeholder="Rechercher une catégorie..."
                                   value={searchTerm}
                                   onChange={(e) => setSearchTerm(e.target.value)}
                                   size="small"
                                   InputProps={{
                                       startAdornment: (
                                           <InputAdornment position="start">
                                               <SearchIcon color="action" />
                                           </InputAdornment>
                                       ),
                                       endAdornment: searchTerm && (
                                           <InputAdornment position="end">
                                               <IconButton onClick={() => setSearchTerm('')} size="small">
                                                   <ClearIcon />
                                               </IconButton>
                                           </InputAdornment>
                                       ),
                                   }}
                               />
                           </div>

                           {filteredCategories.length === 0 ? (
                               <div className={classes.emptyState}>
                                   <CategoryIcon className={classes.emptyIcon} />
                                   <Typography variant="h6" gutterBottom>
                                       {searchTerm ? 'Aucune catégorie trouvée' : 'Aucune catégorie disponible'}
                                   </Typography>
                                   <Typography variant="body2">
                                       {searchTerm 
                                           ? 'Essayez avec d\'autres termes de recherche'
                                           : 'Commencez par ajouter votre première catégorie'
                                       }
                                   </Typography>
                               </div>
                           ) : (
                               <Box>
                                   {filteredCategories.map((category) => (
                                       <Box key={category.id}>
                                           {/* Catégorie principale */}
                                           <div className={`${classes.categoryItem} ${classes.mainCategory}`}>
                                               <div className={classes.categoryContent}>
                                                   <div className={classes.categoryInfo}>
                                                       <div className={classes.hierarchyIndicator}>
                                                           <FolderOpenIcon color="primary" />
                                                       </div>
                                                       {category.image && (
                                                           <Avatar 
                                                               src={category.image} 
                                                               className={classes.categoryImage}
                                                               variant="rounded"
                                                           >
                                                               <ImageIcon />
                                                           </Avatar>
                                                       )}
                                                       {editingId === category.id ? (
                                                           <Box className={classes.editableField} display="flex" gap={2} alignItems="center" flex={1}>
                                                               <TextField
                                                                   value={editingValue}
                                                                   onChange={(e) => setEditingValue(e.target.value)}
                                                                   variant="outlined"
                                                                   size="small"
                                                                   autoFocus
                                                               />
                                                               <input
                                                                   type="file"
                                                                   accept="image/*"
                                                                   onChange={(e) => handleImageChange(e, setEditingImage)}
                                                                   className={classes.fileInput}
                                                                   id={`edit-image-${category.id}`}
                                                               />
                                                               <label htmlFor={`edit-image-${category.id}`}>
                                                                   <IconButton component="span" size="small">
                                                                       <PhotoCameraIcon />
                                                                   </IconButton>
                                                               </label>
                                                           </Box>
                                                       ) : (
                                                           <Typography 
                                                               className={classes.categoryName}
                                                               onClick={() => handleEdit(category.id, category.name, category.image)}
                                                               style={{ cursor: 'pointer', flex: 1 }}
                                                           >
                                                               {category.name}
                                                           </Typography>
                                                       )}
                                                       {category.subCategories && category.subCategories.length > 0 && (
                                                           <Chip 
                                                               label={`${category.subCategories.length} sous-catégorie${category.subCategories.length > 1 ? 's' : ''}`}
                                                               size="small"
                                                               color="secondary"
                                                               variant="outlined"
                                                           />
                                                       )}
                                                   </div>
                                                   <div className={classes.categoryActions}>
                                                       {category.subCategories && category.subCategories.length > 0 && (
                                                           <Tooltip title={expandedCategories[category.id] ? 'Réduire' : 'Développer'}>
                                                               <IconButton
                                                                   className={`${classes.actionButton} ${classes.expandButton}`}
                                                                   onClick={() => toggleExpanded(category.id)}
                                                                   size="small"
                                                               >
                                                                   {expandedCategories[category.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                               </IconButton>
                                                           </Tooltip>
                                                       )}
                                                       
                                                       {editingId === category.id ? (
                                                           <>
                                                               <Tooltip title="Sauvegarder">
                                                                   <IconButton
                                                                       className={`${classes.actionButton} ${classes.saveButton}`}
                                                                       onClick={handleSaveEdit}
                                                                       size="small"
                                                                   >
                                                                       <SaveIcon />
                                                                   </IconButton>
                                                               </Tooltip>
                                                               <Tooltip title="Annuler">
                                                                   <IconButton
                                                                       className={classes.actionButton}
                                                                       onClick={() => setEditingId(null)}
                                                                       size="small"
                                                                   >
                                                                       <ClearIcon />
                                                                   </IconButton>
                                                               </Tooltip>
                                                           </>
                                                       ) : (
                                                           <>
                                                               <Tooltip title="Modifier">
                                                                   <IconButton
                                                                       className={`${classes.actionButton} ${classes.saveButton}`}
                                                                       onClick={() => handleEdit(category.id, category.name, category.image)}
                                                                       size="small"
                                                                   >
                                                                       <EditIcon />
                                                                   </IconButton>
                                                               </Tooltip>
                                                               <Tooltip title="Ajouter une sous-catégorie">
                                                                   <IconButton
                                                                       className={classes.actionButton}
                                                                       onClick={() => toggleForm(category.id, 'add')}
                                                                       size="small"
                                                                       style={{ color: '#f59e0b' }}
                                                                   >
                                                                       <AddIcon />
                                                                   </IconButton>
                                                               </Tooltip>
                                                               <Tooltip title="Supprimer">
                                                                   <IconButton
                                                                       className={`${classes.actionButton} ${classes.deleteButton}`}
                                                                       onClick={() => handleDeleteClick(category, false)}
                                                                       size="small"
                                                                   >
                                                                       <DeleteIcon />
                                                                   </IconButton>
                                                               </Tooltip>
                                                           </>
                                                       )}
                                                   </div>
                                               </div>

                                               {/* Formulaire d'ajout de sous-catégorie */}
                                               {showForm[`add-${category.id}`] && (
                                                   <div className={classes.categoryFormContainer}>
                                                       <Typography variant="subtitle2" gutterBottom>
                                                           Ajouter une sous-catégorie à "{category.name}"
                                                       </Typography>
                                                       <Grid container spacing={2} alignItems="center">
                                                           <Grid item xs={12} sm={4}>
                                                               <TextField
                                                                   placeholder="Nom de la sous-catégorie"
                                                                   variant="outlined"
                                                                   size="small"
                                                                   fullWidth
                                                                   className={classes.textField}
                                                               />
                                                           </Grid>
                                                           <Grid item xs={12} sm={3}>
                                                               <input
                                                                   type="file"
                                                                   accept="image/*"
                                                                   className={classes.fileInput}
                                                                   id={`add-sub-image-${category.id}`}
                                                               />
                                                               <label htmlFor={`add-sub-image-${category.id}`}>
                                                                   <Button component="span" startIcon={<PhotoCameraIcon />} size="small">
                                                                       Image
                                                                   </Button>
                                                               </label>
                                                           </Grid>
                                                           <Grid item xs={12} sm={3}>
                                                               <Button
                                                                   size="small"
                                                                   startIcon={<AddIcon />}
                                                                   className={classes.submitButton}
                                                                   fullWidth
                                                               >
                                                                   Ajouter
                                                               </Button>
                                                           </Grid>
                                                           <Grid item xs={12} sm={2}>
                                                               <Button
                                                                   size="small"
                                                                   onClick={() => toggleForm(category.id, 'add')}
                                                               >
                                                                   Annuler
                                                               </Button>
                                                           </Grid>
                                                       </Grid>
                                                   </div>
                                               )}
                                           </div>

                                           {/* Sous-catégories */}
                                           {renderSubCategories(category.subCategories, category.id)}
                                       </Box>
                                   ))}
                               </Box>
                           )}
                       </Card>
                   </Slide>

                   {/* CategorySelector */}
                   <Slide direction="up" in={true} timeout={1400}>
                       <Card className={classes.categoryCard}>
                           <div className={classes.categoryHeader}>
                               <Typography variant="h6" color="primary">
                                   Sélecteur de catégories
                               </Typography>
                           </div>
                           <CardContent>
                               <CategorySelector />
                           </CardContent>
                       </Card>
                   </Slide>
               </Container>

               {/* Dialog de confirmation de suppression */}
               <Dialog
                   open={deleteDialogOpen}
                   onClose={() => setDeleteDialogOpen(false)}
                   maxWidth="sm"
                   fullWidth
                   PaperProps={{
                       style: {
                           borderRadius: 16,
                           boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                       }
                   }}
               >
                   <DialogTitle>
                       <Box display="flex" alignItems="center" gap={2}>
                           <DeleteIcon color="error" />
                           <Typography variant="h6" color="error">
                               Confirmer la suppression
                           </Typography>
                       </Box>
                   </DialogTitle>
                   <DialogContent>
                       <DialogContentText>
                           {selectedCategory?.isSubCategory ? (
                               <>
                                   Êtes-vous sûr de vouloir supprimer la sous-catégorie{' '}
                                   <strong>{selectedCategory?.name}</strong> ?
                               </>
                           ) : (
                               <>
                                   Êtes-vous sûr de vouloir supprimer la catégorie{' '}
                                   <strong>{selectedCategory?.name}</strong> ?
                                   {selectedCategory?.subCategories?.length > 0 && (
                                       <Box mt={2} p={2} bgcolor="#fff3cd" borderRadius={1}>
                                           <Typography variant="body2" color="textSecondary">
                                               ⚠️ <strong>Attention :</strong> Cette catégorie contient{' '}
                                               {selectedCategory.subCategories.length} sous-catégorie
                                               {selectedCategory.subCategories.length > 1 ? 's' : ''} qui seront également supprimée
                                               {selectedCategory.subCategories.length > 1 ? 's' : ''}.
                                           </Typography>
                                       </Box>
                                   )}
                               </>
                           )}
                           <br />
                           <Typography variant="body2" color="error" style={{ marginTop: 16 }}>
                               Cette action est irréversible.
                           </Typography>
                       </DialogContentText>
                   </DialogContent>
                   <DialogActions style={{ padding: '16px 24px' }}>
                       <Button 
                           onClick={() => setDeleteDialogOpen(false)} 
                           style={{ 
                               borderRadius: 8,
                               textTransform: 'none',
                               fontWeight: 600
                           }}
                       >
                           Annuler
                       </Button>
                       <Button 
                           onClick={handleDelete} 
                           variant="contained"
                           style={{ 
                               backgroundColor: '#ef4444',
                               color: 'white',
                               borderRadius: 8,
                               textTransform: 'none',
                               fontWeight: 600,
                               boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                           }}
                           startIcon={<DeleteIcon />}
                       >
                           Supprimer définitivement
                       </Button>
                   </DialogActions>
               </Dialog>
           </div>
       </ThemeProvider>
   );
}

export default CategoryManager;